import { Link, useLocation } from "react-router";

import { useAuth } from "@/auth/authState";
import { TodoNotice } from "@/components/beheer/TodoNotice";
import { TODO_NEEDS, todoGroups } from "@/components/beheer/todoScreens";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { allNavItems, groupForSection, sectionForPath } from "@/fixtures/navigation";

type PlaceholderProps = {
  title: string;
  /** Waar dit scherm over gaat zodra het er is. */
  description?: string;
  /** Welk prototypescherm of welke module hier komt. */
  origin?: string;
  /** Wat er nog nodig is voordat het scherm gebouwd kan worden (data, besluit, ronde). */
  needs?: string;
};

/**
 * Elk nog niet gebouwd scherm (stijl A, ontwerp 28-10-2026, scherm 36).
 *
 * Bewust géén blanco pagina en géén "404": de route klopt, het scherm komt nog.
 * Wie hier belandt ziet waar hij is, waar het scherm over gaat, wat ervoor
 * nodig is en hoe hij terugkomt; de schil eromheen (zijbalk, kop, tabbalk met
 * het grijze puntje, paginakop) staat er al precies zoals bij een afgerond
 * scherm. Zonder `needs` valt de korte regel uit het overzicht "Nog te bouwen"
 * in (`TODO_NEEDS`).
 */
export function Placeholder({ title, description, origin, needs }: PlaceholderProps) {
  const { pathname } = useLocation();
  const isAdmin = (useAuth().account?.roles ?? []).includes("admin");
  const current = allNavItems.find((item) => item.to === pathname);
  const section = sectionForPath(pathname);
  const group = section ? groupForSection(section) : undefined;
  const nodig = needs ?? TODO_NEEDS[pathname];
  // De andere nog te bouwen schermen in hetzelfde onderdeel, zodat de pagina
  // ook zegt wat er rondom nog komt.
  const buren = (todoGroups().find((groep) => groep.items.some((item) => item.to === pathname))?.items ?? [])
    .filter((item) => item.to !== pathname);

  return (
    <>
      <PageHeader
        title={title}
        description={description}
        breadcrumbs={
          section
            ? [{ label: group?.title ?? "PortefeuilleAtlas" }, { label: section.title }]
            : [{ label: "PortefeuilleAtlas", to: "/" }, { label: current?.label ?? title }]
        }
        exportDisabledReason="Dit scherm is nog niet gebouwd"
      />

      <TodoNotice
        title={title}
        actions={
          <>
            <Link to="/nog-te-bouwen">
              <Button variant="secondary">Alles wat nog komt</Button>
            </Link>
            <Link to="/">
              <Button variant="secondary">Naar het dashboard</Button>
            </Link>
            {isAdmin && (
              <Link to="/styleguide">
                <Button variant="ghost">Naar de stijlgids</Button>
              </Link>
            )}
          </>
        }
      >
        <p>
          {origin ? `${origin} ` : ""}
          {nodig
            ? <>Wat ervoor nodig is: {nodig}</>
            : "De applicatieschil, de tokens en de componenten staan; de inhoud volgt in een latere ronde."}
        </p>
      </TodoNotice>

      {buren.length > 0 && (
        <Card title={`Ook nog te bouwen${section ? ` in ${section.title}` : ""}`}>
          <ul className="atl-todo__buren">
            {buren.map((item) => (
              <li key={item.to}>
                <Link to={item.to}>{item.label}</Link>
                <span>{item.needs}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
